'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Shield, ArrowRight, X } from 'lucide-react';
import { storage } from '@/lib/storage';
import type { IntelligenceReport } from '@/lib/unifiedIntelligence';
import type { ReentryState } from '@/lib/reentryState';

interface Props {
  intelligence: IntelligenceReport | null;
  reentry: ReentryState | null;
}

const DISMISS_KEY = 'deload_banner_dismissed_at';
const DISMISS_COOLDOWN_HOURS = 24;

/**
 * Surfaces the engine's deload recommendation as a prominent dashboard banner.
 *
 * Already-surfaced today: a small amber line inside ProgramDashboard ("Deload
 * recommended: ..."). That's invisible to freestyle users and doesn't show
 * urgency. This banner uses the full `predictions.deloadRecommendation` —
 * urgency, reasoning, recommended date — and renders for both program and
 * freestyle paths.
 *
 * Suppressed while:
 *   - intelligence is null
 *   - deload not needed
 *   - user is in a reentry state (a returning user shouldn't see "deload
 *     recommended" — they need to train, not deload further)
 *   - the user dismissed within the last 24 hours
 *
 * Dismiss writes a timestamp to localStorage; expires after 24h so the next
 * day's banner re-surfaces if the recommendation stands.
 */
export default function DeloadBanner({ intelligence, reentry }: Props) {
  const router = useRouter();
  const [dismissed, setDismissed] = useState(false);

  // Read dismiss timestamp on mount
  useEffect(() => {
    const ts = storage.get(DISMISS_KEY);
    if (!ts) return;
    const then = parseInt(ts, 10);
    if (isNaN(then)) return;
    const hoursAgo = (Date.now() - then) / 3_600_000;
    if (hoursAgo < DISMISS_COOLDOWN_HOURS) {
      setDismissed(true);
    } else {
      // Cooldown expired — clean up
      storage.remove(DISMISS_KEY);
    }
  }, []);

  if (dismissed) return null;
  if (!intelligence) return null;
  if (reentry?.status === 'returning') return null;

  const deload = intelligence.predictions?.deloadRecommendation;
  if (!deload?.needed) return null;

  const urgency = deload.urgency || 'low';

  // Urgency styling — escalating from amber → red as urgency rises.
  let border: string;
  let iconBg: string;
  let label: string;
  let labelColor: string;
  switch (urgency) {
    case 'critical':
      border = 'border-red-500/60';
      iconBg = 'bg-red-500/20 border-red-500/40';
      label = 'Deload required';
      labelColor = 'text-red-400';
      break;
    case 'high':
      border = 'border-orange-500/50';
      iconBg = 'bg-orange-500/20 border-orange-500/40';
      label = 'Deload strongly suggested';
      labelColor = 'text-orange-400';
      break;
    case 'medium':
      border = 'border-amber-500/40';
      iconBg = 'bg-amber-500/20 border-amber-500/40';
      label = 'Consider a deload';
      labelColor = 'text-amber-400';
      break;
    default:
      border = 'border-amber-500/30';
      iconBg = 'bg-amber-500/15 border-amber-500/30';
      label = 'Deload window opening';
      labelColor = 'text-amber-300';
  }

  const dismiss = () => {
    storage.set(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  const startDeloaded = () => {
    // Write the deloadAdjustment that the session page reads. Multipliers
    // mirror the smartWorkoutGenerator deload gate (50% volume, 70% intensity).
    storage.set('deloadAdjustment', JSON.stringify({
      volumeMultiplier: 0.5,
      intensityMultiplier: 0.7,
      reason: deload.reasoning || `Deload (${urgency} urgency) triggered from dashboard banner`,
      appliedAt: Date.now(),
    }));
    router.push('/session');
  };

  return (
    <div className={`rounded-xl border-2 ${border} bg-operator-void/60 px-4 py-3`} role="alert">
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg border ${iconBg} flex items-center justify-center flex-shrink-0`}>
          <Shield className={`w-5 h-5 ${labelColor}`} aria-hidden />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-[10px] font-bold uppercase tracking-[0.22em] ${labelColor} mb-0.5`}>
            {label}
          </p>
          <p className="text-sm font-semibold text-white leading-tight">
            {deload.reasoning || 'Fatigue indicators are climbing — back off this week to protect performance.'}
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-2">
            <button
              onClick={startDeloaded}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider transition ${
                urgency === 'critical' || urgency === 'high'
                  ? 'bg-orange-500 text-orange-950 hover:bg-orange-400'
                  : 'bg-amber-500 text-amber-950 hover:bg-amber-400'
              }`}
            >
              Start deloaded session
              <ArrowRight className="w-3 h-3" aria-hidden />
            </button>
            {deload.recommendedDate && (
              <span className="text-[11px] text-slate-400">
                Suggested by {new Date(deload.recommendedDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
              </span>
            )}
          </div>
        </div>
        <button
          onClick={dismiss}
          className="text-slate-500 hover:text-slate-300 flex-shrink-0 p-1"
          aria-label="Dismiss for 24 hours"
        >
          <X className="w-4 h-4" aria-hidden />
        </button>
      </div>
    </div>
  );
}
